import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
      <div className="bg-white rounded-lg shadow-sm p-8 prose prose-sm max-w-none">
        <p className="text-sm text-gray-500 mb-6">
          <strong>Effective Date:</strong> November 2, 2025<br />
          <strong>Last Updated:</strong> November 2, 2025
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          This Privacy Policy describes how Urbanix LLC, a Michigan limited liability company doing business as Pay2Rent.me 
          ("Pay2Rent," "we," "us," or "our"), collects, uses, and protects personal information from users ("you," "your," 
          or "users") who access or use our website and related services (collectively, the "Services").
        </p>
        <p className="text-gray-700 mb-4">
          By accessing or using the Services, you agree to this Privacy Policy. If you do not agree, please do not use our Services.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
        <p className="text-gray-700 mb-4">
          We collect the following categories of information:
        </p>
        
        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">a. Information You Provide Directly</h3>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li><strong>Account Information:</strong> Name, email address, and any optional profile details you provide.</li>
          <li><strong>Listing and Rental Data:</strong> Information you provide when creating listings, communicating with other users, or completing rental transactions.</li>
          <li><strong>Communications:</strong> Messages and correspondence between users, or between you and Pay2Rent support.</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">b. Information Collected Automatically</h3>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li><strong>Device and Usage Data:</strong> IP address, browser type, operating system, access times, and referring URLs.</li>
          <li><strong>Analytics and Cookies:</strong> We use analytics tools (e.g., Google Analytics) to understand site usage and performance.</li>
          <li><strong>Security Tools:</strong> Google reCAPTCHA is used to prevent fraud and abuse.</li>
        </ul>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">c. Payment Information</h3>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Pay2Rent does not collect or store credit card or banking information.</li>
          <li>Payments are processed securely through Stripe Connect and PayPal, each governed by their respective privacy policies.</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
        <p className="text-gray-700 mb-4">
          We use your information for the following purposes:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>To provide, operate, and maintain the Services.</li>
          <li>To facilitate user-to-user transactions and rental communications.</li>
          <li>To manage your account, listings, and preferences.</li>
          <li>To ensure compliance with applicable laws and platform policies.</li>
          <li>To analyze usage and improve the Services.</li>
          <li>To detect, prevent, and address fraud, security issues, or misuse.</li>
          <li>To communicate with you about your account, updates, or policy changes.</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Legal Basis for Processing (EU/UK Users)</h2>
        <p className="text-gray-700 mb-4">
          For users located in the European Union, United Kingdom, or other jurisdictions that require a lawful basis for processing, we rely on one or more of the following:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li><strong>Contractual necessity:</strong> To provide and operate the Services.</li>
          <li><strong>Legal obligation:</strong> To comply with applicable laws or requests.</li>
          <li><strong>Legitimate interests:</strong> For security, analytics, and improving our Services.</li>
          <li><strong>Consent:</strong> For certain cookies, marketing, or optional data you choose to share.</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Sharing and Disclosure of Information</h2>
        <p className="text-gray-700 mb-4">
          We may share information in the following circumstances:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li><strong>Service Providers:</strong> With trusted third-party vendors who perform services on our behalf (e.g., AWS for hosting, Stripe and PayPal for payments, Google for analytics and security).</li>
          <li><strong>Legal Compliance:</strong> When required by law, subpoena, or government request.</li>
          <li><strong>Protection of Rights:</strong> To enforce our Terms, protect against fraud, or ensure platform integrity.</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of company assets.</li>
          <li><strong>With Other Users:</strong> Limited information (such as name or contact method) may be shared with other users as needed to facilitate rentals.</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Data Retention</h2>
        <p className="text-gray-700 mb-4">
          We retain personal data only as long as necessary to:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Fulfill the purposes described in this Policy.</li>
          <li>Comply with legal, tax, or accounting obligations.</li>
          <li>Resolve disputes or enforce our agreements.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          When no longer required, information is securely deleted or anonymized.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. International Transfers</h2>
        <p className="text-gray-700 mb-4">
          We are based in the United States, and your data may be stored or processed in the U.S. or other jurisdictions. 
          By using the Services, you acknowledge that your information may be transferred to and processed in countries 
          with different data protection standards than your own.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Security</h2>
        <p className="text-gray-700 mb-4">
          We implement appropriate administrative, technical, and physical safeguards to protect your personal information 
          from unauthorized access, alteration, or destruction. However, no system is entirely secure, and we cannot 
          guarantee absolute protection.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Your Rights and Choices</h2>
        <p className="text-gray-700 mb-4">
          Depending on your jurisdiction, you may have the following rights:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Access, correction, or deletion of your personal data.</li>
          <li>Restriction or objection to processing.</li>
          <li>Data portability.</li>
          <li>Withdrawal of consent (where processing is based on consent).</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Requests may be made by contacting <a href="mailto:support@pay2rent.me" className="text-primary-600 hover:text-primary-700">support@pay2rent.me</a>. 
          We may need to verify your identity before fulfilling certain requests.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Cookies and Analytics</h2>
        <p className="text-gray-700 mb-4">
          We use cookies and similar technologies for:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Website functionality and session management.</li>
          <li>Security and fraud prevention.</li>
          <li>Analytics to understand user behavior and improve the Service.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          You may adjust your browser settings to refuse cookies, though some site features may not function properly.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Third-Party Links</h2>
        <p className="text-gray-700 mb-4">
          Our website may include links to third-party sites or services. We are not responsible for the privacy practices 
          or content of those third parties.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Children's Privacy</h2>
        <p className="text-gray-700 mb-4">
          The Services are intended for users 18 years of age or older. We do not knowingly collect personal data from 
          children under 18. If you believe a minor has provided information, please contact us to remove it.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Changes to This Policy</h2>
        <p className="text-gray-700 mb-4">
          We may update this Privacy Policy periodically. The "Last Updated" date at the top will reflect the most recent 
          revision. Material changes will be communicated through the website or via email.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact Us</h2>
        <p className="text-gray-700 mb-4">
          For questions, concerns, or data requests, please contact:
        </p>
        <p className="text-gray-700 mb-4">
          <strong>Urbanix LLC (d/b/a Pay2Rent.me)</strong><br />
          Email: <a href="mailto:support@pay2rent.me" className="text-primary-600 hover:text-primary-700">support@pay2rent.me</a><br />
          Jurisdiction: Michigan, United States
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">15. California Privacy Rights (CCPA / CPRA Addendum)</h2>
        <p className="text-gray-700 mb-4">
          This section applies to residents of the State of California, in accordance with the California Consumer Privacy 
          Act (CCPA) and California Privacy Rights Act (CPRA).
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">a. Categories of Personal Information Collected</h3>
        <p className="text-gray-700 mb-4">
          In the past 12 months, we have collected the following categories of personal information:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Identifiers (name, email address, IP address).</li>
          <li>Commercial information (rental history and transaction data).</li>
          <li>Internet or network activity (device data, browsing data).</li>
          <li>Geolocation data (approximate region inferred from IP).</li>
          <li>Inferences from other information (preferences, interests).</li>
        </ul>
        <p className="text-gray-700 mb-4">
          We do not sell or share personal information for monetary consideration.
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">b. Purposes of Use</h3>
        <p className="text-gray-700 mb-4">
          We collect personal information for the business purposes described in Section 3 of this Policy.
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">c. Rights of California Residents</h3>
        <p className="text-gray-700 mb-4">
          Subject to applicable law, California residents may have the right to:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Request disclosure of the categories and specific pieces of personal information collected.</li>
          <li>Request deletion of personal information, subject to legal exceptions.</li>
          <li>Request correction of inaccurate personal information.</li>
          <li>Opt out of the sale or sharing of personal information (we do not currently sell or share personal data).</li>
          <li>Limit the use or disclosure of sensitive personal information (we do not collect sensitive personal data).</li>
          <li>Request information about how we respond to "Do Not Track" signals (we do not track users across third-party sites).</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Requests may be submitted by email to <a href="mailto:support@pay2rent.me" className="text-primary-600 hover:text-primary-700">support@pay2rent.me</a>. 
          We will respond in accordance with applicable California law and may require reasonable verification of your identity.
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">d. Non-Discrimination</h3>
        <p className="text-gray-700 mb-4">
          We will not discriminate against you for exercising any of your CCPA/CPRA rights.
        </p>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-gray-700">
            <strong>Urbanix LLC (d/b/a Pay2Rent.me)</strong><br />
            Michigan, United States<br />
            Contact: <a href="mailto:support@pay2rent.me" className="text-primary-600 hover:text-primary-700">support@pay2rent.me</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

